import { useAuth0 } from "@auth0/auth0-react";
import { Link } from "react-router-dom";
import { ChangeEvent, useEffect, useState } from "react";
import { AuthSource } from "../../services/AuthService";
import { useDispatch } from "react-redux";
import { useAuth } from "../../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { validatePassword } from "../shared/uiHelpers";
import { validateEmail} from "../shared/responseValidators";
import { Toaster, toast } from "react-hot-toast";
import { toastFail, toastSuccess } from "../../config";
import "../../css/Icons.css";
import "./Login.css";
import { selectIsMobile, setSignUpNow } from "../../redux/features/appData";
import {
  apiAuthSendVerificationCode,
  apiAuthVerifyCode,
  apiAuthUpdatePassword,
} from "../../services/AuthService";
import config from "environment";
import { useSelector } from "react-redux";
import { EFFNO_LOGO } from "../shared/media";

export default function Login() {
  const auth = useAuth();
  const navigate = useNavigate();

  const { loginWithRedirect } = useAuth0();

  // if we are logging in we are not signing up
  const dispatch = useDispatch();
  dispatch(setSignUpNow(false));

  const [email, setEmail] = useState<string | undefined>();
  const [password, setPassword] = useState<string | undefined>();
  const [errorText, setErrorText] = useState<string>("");
  const [showForgot, setShowForgot] = useState<boolean>(false);
  const [showVerifyCode, setShowVerifyCode] = useState<boolean>(false);
  const [code, setCode] = useState<string>("");
  const [showPasswordChange, setShowPasswordChange] = useState<boolean>(false);
  const isMobile = useSelector(selectIsMobile);

  useEffect(() => {
    if (isMobile) {
      setErrorText("Mobile not supported.");
    }
    const urlError = new URLSearchParams(window.location.search).get("error");

    // we get her primarily when someone tries to sign in w. Oauth for a user that
    // is not in the DB

    if (urlError) {
      setErrorText("That is not a valid user.");
    }
  }, []);

  const submitStandardLogin = async () => {
    if (email && password) {
      const loginResult = await auth.login({
        email,
        passwordKey: password,
        authSource: AuthSource.STANDARD,
      });

      if (!loginResult.success) {
        setErrorText(loginResult?.error ? loginResult.error : "Login failed");
      }
    } else {
      setErrorText("Please enter a valid email and password.");
    }
  };

  const handleForgot = () => {
    setShowForgot(true);
  };

  const handleSendVerification = async () => {
    if (!validateEmail(email) || !email) {
      setErrorText("Invalid email format.");
      return;
    }

    toast("Verification code sent.", toastSuccess);
    setShowVerifyCode(true);
    const result = await apiAuthSendVerificationCode(email);
  };

  const handleChangeCode = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    if (!value || value.length < 7) {
      setCode(value);
    }
  };

  const handleVerifyCode = async () => {
    setErrorText("");

    if (email && code.length === 6) {
      const result = await apiAuthVerifyCode(email, code);

      if (!result) {
        setErrorText("Invalid or expired code.");
        return;
      }

      setPassword("");
      setShowPasswordChange(true);
    }
  };

  const handlePassword = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setPassword(value);

    validatePassword(value)
      ? setErrorText("")
      : setErrorText("Passwords need min 8 chars, a capital and a number.");
  };

  // expects a valid password
  const handleUpdatePassword = async () => {
    if (!email || !password || !code) {
      toast("Error updatng password, please try again.", toastFail);
      return;
    }

    const result = await apiAuthUpdatePassword(email, password, code);

    if (!result) {
      toast("Error updatng password, please try again.", toastFail);
      return;
    }

    // reset everything
    setPassword("");
    setErrorText("");
    setCode("");
    setShowForgot(false);
    setShowVerifyCode(false);
    setShowPasswordChange(false);
  };

  const handleResend = async () => {
    setErrorText("");

    if (!email) {
      toast("Error updatng password, please try again.", toastFail);
      return;
    }

    const result = await apiAuthSendVerificationCode(email);

    if (!result) {
      toast("Error updatng password, please try again.", toastFail);
      return;
    }

    toast("New code has been sent", toastSuccess);

    return;
  };

  const handleReset = () => {
    // reset everything
    setPassword("");
    setErrorText("");
    setCode("");
    setShowForgot(false);
    setShowVerifyCode(false);
    setShowPasswordChange(false);
  };

  return (
    <>
      <div className={config.theme} style={{ width: "100%", height: "100%" }}>
        <div id="login">
          <div className="signup-header">
            <div 
              className="signup-header-logo"
              onClick={() => window.open("https://effno.co", "_blank")}
            >
              <img src={EFFNO_LOGO.REGULAR} />
            </div>
            <div className="signup-header-buttons">
              Or&nbsp;
              <button
                className="signin-header-button"
                onClick={() => {
                  navigate("/signup");
                }}>
                Sign Up
              </button>
            </div>
          </div>
          {/* <div className="signin-header">
            <button
              className="signin-header-button"
              onClick={() => {
                navigate("/signup");
              }}>
              Sign Up
            </button>
          </div> */}
          {!showForgot && (
            <div className="signin-inside">
              <div className="signin-title">
                <div className="signin-title-left">
                  <h1>
                    Sign In!
                    {/* <br />
                    We're glad you're here. */}
                  </h1>
                </div>

                <div className="signin-title-right">
                  Don't have an account?&nbsp;<Link to="/signup">Sign Up</Link>.
                </div>
              </div>
              <div className="signin-email">
                <input
                  type="text"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    const value = event.target.value;
                    setEmail(value.trim());
                  }}
                />
              </div>
              <div className="signin-password">
                <input
                  type="password"
                  value={password}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    const value = event.target.value;
                    setPassword(value);
                  }}
                  placeholder="Enter your password"
                />
              </div>
              <div className="signin-submit">
                <button
                  className="signin-submit-button"
                  onClick={submitStandardLogin}
                >
                  Sign In
                </button>
              </div>

              <div className="signin-forgot" onClick={handleForgot}>
                Forgot your password
              </div>

              <div className="signin-error">{errorText}</div>

              {/* <div className="signin-divider">&nbsp;</div>

              <div className="signin-oauth">
                <button
                  className="signin-oauth-button"
                  onClick={() => loginWithRedirect({})}>
                  <div className="signin-button">
                    <div className="signin-button-logo">
                      <img className="icon-logo-google" />
                    </div>
                    <div className="signin-button-text">
                      Continue with Google
                    </div>
                  </div>
                </button>
              </div> */}

              {/* <div className="signin-text">
                Don't have an account? <Link to="/signup">Sign Up</Link>.
              </div> */}

              {/* <div className="signin-disclaimer">
                By continuing with Google, you agree to effno's terms &
                Conditions and Privacy Policy
              </div> */}

              <div className="signin-container"></div>
            </div>
          )}
          {showForgot && !showVerifyCode && (
            <div className="signin-inside">
              <h1>Forgot your Password?</h1>
              Enter the email address you used for your account. We'll send you
              a confirmation code that you can enter to reset your password.
              <div className="signin-email">
                <input
                  type="text"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    const value = event.target.value;
                    setEmail(value);
                  }}
                />
              </div>
              <div className="signin-submit">
                <button
                  className="signin-submit-button"
                  onClick={handleSendVerification}>
                  Send A Verification Code
                </button>
              </div>
              <div className="signin-remember">
                Nevermind, I remember now. &nbsp;{" "}
                <a onClick={handleReset}>Take me back!</a>
              </div>
              <div className="signin-error">{errorText}</div>
            </div>
          )}
          {showForgot && showVerifyCode && !showPasswordChange && (
            <div className="signin-inside">
              <h1>Verify your code</h1>
              Enter the 6 digit code we sent to your email.
              <div className="signin-email">
                <input
                  type="text"
                  placeholder="Enter code here"
                  value={code}
                  onChange={handleChangeCode}
                />
              </div>
              <div className="signin-submit">
                <button
                  className="signin-submit-button"
                  onClick={handleVerifyCode}
                  disabled={code.length !== 6}>
                  Confirm Code
                </button>
              </div>
              <div className="signin-remember">
                <a onClick={handleResend}>Re-send code</a>
                &nbsp; or nevermind, &nbsp;
                <a onClick={handleReset}>take me back.</a>
              </div>
              <div className="signin-error">{errorText}</div>
            </div>
          )}
          {showVerifyCode && showPasswordChange && (
            <div className="signin-inside">
              <h1>Change your password</h1>
              Enter a new password. 8 characters minimum with at least one
              number and one capital.
              <div className="signin-email">
                <input
                  type="password"
                  placeholder="Enter password..."
                  value={password}
                  onChange={handlePassword}
                />
              </div>
              <div className="signin-submit">
                <button
                  className="signin-submit-button"
                  onClick={handleUpdatePassword}
                  disabled={!validatePassword(password)}>
                  Update Password
                </button>
              </div>
              <div className="signin-error">{errorText}</div>
            </div>
          )}
        </div>
      </div>
      <Toaster />
    </>
  );
}
